import * as icons from '@mui/icons-material';
import { Badge, Box, IconButton, Paper, Popover, Toolbar } from '@mui/material';
import {
  type DataGridProps as MuiDataGridProps,
  DataGrid as MuiDataGrid,
  type GridColDef,
} from '@mui/x-data-grid';
import { type UseDataGridReturnType } from '@refinedev/mui';
import { useMemo, useState } from 'react';
import { FilterList } from './filters/FilterList.tsx';

type Props<T extends object> = {
  columns: Array<GridColDef<T>>;
  filters: UseDataGridReturnType['filters'];
  setFilters: UseDataGridReturnType['setFilters'];
  defaultFilterColumn?: string;
} & MuiDataGridProps<T>;

export type { GridColDef };

export const DataGrid = <T extends object>({
  columns,
  filters,
  setFilters,
  filterDebounceMs = 800,
  defaultFilterColumn,
  ...props
}: Props<T>) => {
  // MuiDataGridのフィルタは利用しないので、muiに渡すcolumnsのみfilterableをfalseで上書きする
  const columnsForMui = useMemo(
    () => columns.map((column) => ({ ...column, filterable: false })),
    [columns],
  );
  const filterableColumns = useMemo(
    () => columns.filter((column) => column.filterable !== false),
    [columns],
  );
  const [filterListAnchorEl, setFilterListAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <Box>
      <Toolbar variant="dense" sx={{ justifyContent: 'flex-end' }}>
        <IconButton onClick={(e) => setFilterListAnchorEl(e.currentTarget)}>
          <Badge invisible={filters.length === 0} variant="dot" color="primary">
            <icons.FilterListOutlined />
          </Badge>
        </IconButton>
      </Toolbar>
      <MuiDataGrid<T>
        {...props}
        columns={columnsForMui}
        filterDebounceMs={filterDebounceMs}
        filterModel={undefined}
        onFilterModelChange={undefined}
      />
      {filterableColumns.length > 0 && (
        <Popover
          open={filterListAnchorEl !== null}
          anchorEl={filterListAnchorEl}
          onClose={() => setFilterListAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
          <Paper sx={{ p: 1, width: '50dvw' }}>
            <FilterList
              columns={columns}
              filters={filters}
              setFilters={setFilters}
              debounceMs={filterDebounceMs}
              defaultColumnName={defaultFilterColumn}
            />
          </Paper>
        </Popover>
      )}
    </Box>
  );
};
